import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';

/**
 * Decorative "stacked cards" frame — ports the legacy `.badgeCard_LP` look from
 * the .NET site, where two rotated layers peek out behind a badge card so it
 * reads as a stack of cards.
 *
 * Wrap any Card / BadgeCard. The child should fill its height (`height: '100%'`)
 * so the frame and card line up in a grid. The two layer colours come from the
 * theme (`palette.stack.near` / `.far`) so the stack flips with the light/dark
 * toggle. The layers are purely decorative and never intercept pointer events.
 *
 * Pass `enabled={false}` to skip the decoration. Only Multi Badges ('learningpath')
 * should read as a stack — see isMultiBadge() in constants/badgeTypes. The disabled
 * branch keeps the identical positioning wrapper so grid layout is unchanged either way.
 */
export default function StackedCard({ children, enabled = true }: { children: ReactNode; enabled?: boolean }) {
    const { palette, shape } = useTheme();
    const radius = `${shape.borderRadius}px`;

    if (!enabled) {
        return <Box sx={{ position: 'relative', height: '100%' }}>{children}</Box>;
    }

    return (
        <Box sx={{ position: 'relative', height: '100%' }}>
            {/* Decorative layers — `::before` is the far layer, `::after` the near
                one (paints on top of `::before`, still below the card). The whole
                element sits at zIndex 0, so it stays behind the card regardless of
                each pseudo-element's own paint order. */}
            <Box
                aria-hidden
                data-testid="stacked-frame"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 0,
                    pointerEvents: 'none',
                    // Far layer (purple)
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: '20px',
                        top: '-1px',
                        width: '90%',
                        height: '98%',
                        borderRadius: radius,
                        background: palette.stack.far,
                        boxShadow: '0 5px 14px rgba(0,0,0,0.20)',
                        transform: 'rotate(-6.15deg)',
                    },
                    // Near layer (periwinkle)
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: '16px',
                        top: 0,
                        width: '91%',
                        height: '99%',
                        borderRadius: radius,
                        background: palette.stack.near,
                        boxShadow: '0 5px 11px rgba(0,0,0,0.21)',
                        transform: 'rotate(-3.15deg)',
                    },
                }}
            />
            <Box sx={{ position: 'relative', zIndex: 1, height: '100%' }}>
                {children}
            </Box>
        </Box>
    );
}
